import { CopyOutlined, ReloadOutlined, TableOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { message } from '@client/components/_ui/feedback/message';
import { ExternalInlineLink } from '@client/components/Common/Link';
import { createColumnHelper, PrismaTable, useClientSidePagination, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { createFileRoute } from '@tanstack/react-router';
import {
    Badge,
    Card,
    Checkbox,
    Col,
    Collapse,
    Divider,
    Form,
    InputNumber,
    Radio,
    Row,
    Select,
    Space,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import { DateTime } from 'luxon';
import { useCallback, useMemo, useState } from 'react';

const { Text, Paragraph } = Typography;
const { Panel } = Collapse;

export const Route = createFileRoute('/console/$orgId/component-showcase/table-showcase')({
    staticData: {
        breadcrumb: { title: 'Table component showcase' },
        featureFlag: 'enable-debug-info',
        ui: {
            title: 'PrismaTableV2 Component Showcase',
            subtitle: 'Interactive demonstration of table configurations and features',
        },
    },
    component: TableShowcase,
});

// Sample data types
interface SampleData {
    id: number;
    name: string;
    email: string;
    role: string;
    department: string;
    status: 'active' | 'inactive' | 'pending';
    joinDate: Date;
    salary: number;
    performance: number;
    tags: string[];
    location: string;
    manager: string;
    projects: number;
}

// Generate sample data
function generateSampleData(count: number): SampleData[] {
    const departments = ['Engineering', 'Sales', 'Marketing', 'HR', 'Finance', 'Operations'];
    const roles = ['Developer', 'Manager', 'Analyst', 'Designer', 'Consultant', 'Director'];
    const statuses: Array<'active' | 'inactive' | 'pending'> = ['active', 'inactive', 'pending'];
    const locations = ['New York', 'London', 'Tokyo', 'Sydney', 'Berlin', 'Toronto'];
    const tags = ['Remote', 'On-site', 'Hybrid', 'Contract', 'Full-time', 'Part-time'];
    const firstNames = ['John', 'Jane', 'Mike', 'Sarah', 'David', 'Emma', 'Tom', 'Lisa', 'Chris', 'Anna'];
    const lastNames = ['Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis'];

    return Array.from({ length: count }, (_, i) => {
        const firstName = firstNames[Math.floor(Math.random() * firstNames.length)];
        const lastName = lastNames[Math.floor(Math.random() * lastNames.length)];
        const name = `${firstName} ${lastName}`;

        return {
            id: i + 1,
            name,
            email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}${i}@example.com`,
            role: roles[Math.floor(Math.random() * roles.length)],
            department: departments[Math.floor(Math.random() * departments.length)],
            status: statuses[Math.floor(Math.random() * statuses.length)],
            joinDate: DateTime.now()
                .minus({ days: Math.floor(Math.random() * 1000) })
                .toJSDate(),
            salary: Math.floor(Math.random() * 100000) + 50000,
            performance: Math.floor(Math.random() * 100),
            tags: tags.filter(() => Math.random() > 0.6).slice(0, Math.floor(Math.random() * 3) + 1),
            location: locations[Math.floor(Math.random() * locations.length)],
            manager: `${firstNames[Math.floor(Math.random() * firstNames.length)]} ${
                lastNames[Math.floor(Math.random() * lastNames.length)]
            }`,
            projects: Math.floor(Math.random() * 10) + 1,
        };
    });
}

// Column keys type
type ColumnKey =
    | 'id'
    | 'name'
    | 'email'
    | 'status'
    | 'role'
    | 'department'
    | 'location'
    | 'joinDate'
    | 'salary'
    | 'performance'
    | 'manager'
    | 'projects'
    | 'tags'
    | 'actions';

// Configuration state interface
interface TableConfig {
    // Pagination
    paginationPosition: 'top' | 'bottom' | 'both' | 'none';
    paginationVariant: 'standard' | 'simple' | 'compact';
    showSizeChanger: boolean;
    showQuickJumper: boolean;
    showTotal: boolean;
    defaultPageSize: number;

    // Column visibility
    columnVisibilityEnabled: boolean;
    enableReordering: boolean;
    defaultHidden: ColumnKey[];

    // Row selection
    rowSelectionEnabled: boolean;
    selectionType: 'checkbox' | 'radio';
    preserveSelectedRowKeys: boolean;

    // Refresh
    refreshEnabled: boolean;
    autoRefreshInterval: number;

    // Table appearance
    size: 'small' | 'middle' | 'large';
    bordered: boolean;
    sticky: boolean;
    showHeader: boolean;
    showSorterTooltip: boolean;

    // Expandable
    expandableEnabled: boolean;
}

const defaultConfig: TableConfig = {
    paginationPosition: 'both',
    paginationVariant: 'standard',
    showSizeChanger: true,
    showQuickJumper: false,
    showTotal: true,
    defaultPageSize: 20,
    columnVisibilityEnabled: true,
    enableReordering: true,
    defaultHidden: ['salary', 'manager'] as ColumnKey[],
    rowSelectionEnabled: true,
    selectionType: 'checkbox',
    preserveSelectedRowKeys: true,
    refreshEnabled: true,
    autoRefreshInterval: 0,
    size: 'small',
    bordered: true,
    sticky: false,
    showHeader: true,
    showSorterTooltip: false,
    expandableEnabled: false,
};

// Preset configurations
const presets: Record<string, { name: string; config: TableConfig }> = {
    minimal: {
        name: 'Minimal',
        config: {
            ...defaultConfig,
            paginationPosition: 'bottom' as const,
            paginationVariant: 'simple' as const,
            showSizeChanger: false,
            columnVisibilityEnabled: false,
            rowSelectionEnabled: false,
            refreshEnabled: false,
            bordered: false,
        },
    },
    full: {
        name: 'Full Features',
        config: {
            ...defaultConfig,
            showQuickJumper: true,
            expandableEnabled: true,
            sticky: true,
        },
    },
    compact: {
        name: 'Compact',
        config: {
            ...defaultConfig,
            paginationVariant: 'compact' as const,
            size: 'small' as const,
            showTotal: false,
        },
    },
};

function TableShowcase() {
    const { formatDate, formatCurrency } = useFormatter();
    const [config, setConfig] = useState<TableConfig>(defaultConfig);
    const [form] = Form.useForm();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [topLeftContent, setTopLeftContent] = useState<'none' | 'button' | 'custom'>('none');

    // Generate sample data
    const sampleData = useMemo(() => generateSampleData(150), []);

    // Use client-side pagination hook
    const { paginatedData, total, onParamsChange, params } = useClientSidePagination(
        sampleData,
        config.defaultPageSize,
    );

    // Create column helper
    const columnHelper = createColumnHelper<SampleData, any>();

    // Define columns
    const columns = useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
                cell: ({ value }) => <Text strong>#{value}</Text>,
                width: 80,
                fixed: 'left',
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
                cell: ({ value }) => <Text>{value}</Text>,
                width: 150,
                hideable: false,
            }),
            columnHelper.accessor('email', {
                id: 'email',
                header: 'Email',
                sortable: 'email',
                cell: ({ value }) => (
                    <ExternalInlineLink href={`mailto:${value}`} target="_self">
                        {value}
                    </ExternalInlineLink>
                ),
                width: 200,
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                cell: ({ value }) => {
                    const color = value === 'active' ? 'success' : value === 'pending' ? 'warning' : 'error';
                    const text = value.charAt(0).toUpperCase() + value.slice(1);
                    return <Badge status={color} text={text} />;
                },
                width: 120,
                align: 'center',
            }),
            columnHelper.accessor('role', {
                id: 'role',
                header: 'Role',
                sortable: 'role',
                cell: ({ value }) => (
                    <Tag variant="outlined" color="blue">
                        {value}
                    </Tag>
                ),
                width: 120,
            }),
            columnHelper.accessor('department', {
                id: 'department',
                header: 'Department',
                sortable: 'department',
                cell: ({ value }) => <Text>{value}</Text>,
                width: 130,
            }),
            columnHelper.accessor('location', {
                id: 'location',
                header: 'Location',
                sortable: 'location',
                cell: ({ value }) => <Text>{value}</Text>,
                width: 120,
            }),
            columnHelper.accessor('joinDate', {
                id: 'joinDate',
                header: 'Join Date',
                sortable: 'joinDate',
                cell: ({ value }) => <Text>{DateTime.fromJSDate(value).toFormat('dd MMM yyyy')}</Text>,
                width: 120,
            }),
            columnHelper.accessor('salary', {
                id: 'salary',
                header: 'Salary',
                sortable: 'salary',
                cell: ({ value }) => <Text>{formatCurrency(value)}</Text>,
                width: 120,
                align: 'right',
            }),
            columnHelper.accessor('performance', {
                id: 'performance',
                header: 'Performance',
                sortable: 'performance',
                cell: ({ value }) => (
                    <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 rounded-full bg-muted">
                            <div className="h-2 rounded-full bg-blue-600" style={{ width: `${value}%` }} />
                        </div>
                        <Text className="text-xs">{value}%</Text>
                    </div>
                ),
                width: 150,
            }),
            columnHelper.accessor('manager', {
                id: 'manager',
                header: 'Manager',
                sortable: 'manager',
                cell: ({ value }) => <Text>{value}</Text>,
                width: 150,
            }),
            columnHelper.accessor('projects', {
                id: 'projects',
                header: 'Projects',
                sortable: 'projects',
                cell: ({ value }) => <Badge count={value} showZero color="blue" />,
                width: 100,
                align: 'center',
            }),
            columnHelper.accessor('tags', {
                id: 'tags',
                header: 'Tags',
                sortable: false,
                cell: ({ value }) => (
                    <Space size={4} wrap>
                        {value.map((tag: string) => (
                            <Tag variant="outlined" key={tag} color="default">
                                {tag}
                            </Tag>
                        ))}
                    </Space>
                ),
                width: 200,
            }),
            columnHelper.actions({
                header: 'Actions',
                width: 120,
                cell: () => (
                    <Space>
                        <Button type="link" size="small">
                            Edit
                        </Button>
                        <Button type="danger" size="small">
                            Delete
                        </Button>
                    </Space>
                ),
            }),
        ],
        [columnHelper, formatDate, formatCurrency],
    );

    // Use the table hook
    const tableProps = usePrismaTable({
        data: paginatedData,
        total,
        loading: isRefreshing,
        columns,
        rowKey: 'id',
        queryParams: params,
        pagination: {
            position: config.paginationPosition,
            variant: config.paginationVariant,
            showSizeChanger: config.showSizeChanger,
            showQuickJumper: config.showQuickJumper,
            showTotal: config.showTotal,
            defaultPageSize: config.defaultPageSize,
        },
        onParamsChange,
        columnVisibility: config.columnVisibilityEnabled
            ? {
                  enabled: true,
                  persistKey: 'table-showcase-columns',
                  enableReordering: config.enableReordering,
                  defaultHidden: config.defaultHidden,
              }
            : undefined,
        rowSelection: config.rowSelectionEnabled
            ? {
                  enabled: true,
                  type: config.selectionType,
                  preserveSelectedRowKeys: config.preserveSelectedRowKeys,
              }
            : undefined,
        refreshable: config.refreshEnabled
            ? {
                  enabled: true,
                  onRefresh: async () => {
                      setIsRefreshing(true);
                      await new Promise((resolve) => setTimeout(resolve, 1000));
                      setIsRefreshing(false);
                      message.success('Table refreshed!');
                  },
                  autoRefreshInterval: config.autoRefreshInterval,
              }
            : undefined,
        expandable: config.expandableEnabled
            ? {
                  expandedRowRender: (record) => (
                      <div className="bg-muted p-4">
                          <Paragraph>
                              <strong>Additional Details for {record.name}:</strong>
                          </Paragraph>
                          <Paragraph>
                              Employee #{record.id} has been with us since{' '}
                              {DateTime.fromJSDate(record.joinDate).toFormat('dd MMM yyyy')}. They work in the{' '}
                              {record.department} department as a {record.role}. Current performance rating:{' '}
                              {record.performance}%
                          </Paragraph>
                      </div>
                  ),
              }
            : undefined,
        size: config.size,
        bordered: config.bordered,
        sticky: config.sticky,
        showHeader: config.showHeader,
        showSorterTooltip: config.showSorterTooltip,
    });

    // Handle config changes
    const handleConfigChange = useCallback((_changedValues: Partial<TableConfig>, allValues: TableConfig) => {
        setConfig(allValues);
    }, []);

    // Copy configuration to clipboard
    const copyConfig = useCallback(() => {
        const configStr = JSON.stringify(config, null, 2);
        navigator.clipboard.writeText(configStr);
        message.success('Configuration copied to clipboard!');
    }, [config]);

    // Reset to default configuration
    const resetConfig = useCallback(() => {
        setConfig(defaultConfig);
        form.setFieldsValue(defaultConfig);
        message.info('Configuration reset to default');
    }, [form]);

    // Apply preset
    const applyPreset = useCallback(
        (presetKey: string) => {
            const preset = presets[presetKey];
            if (preset) {
                setConfig(preset.config);
                form.setFieldsValue(preset.config);
                message.success(`Applied "${preset.name}" preset`);
            }
        },
        [form],
    );

    // Get custom top left content
    const getTopLeftContent = useMemo(() => {
        switch (topLeftContent) {
            case 'button':
                return (
                    <Space>
                        <Button size="small" type="primary">
                            Export
                        </Button>
                        <Button size="small">Import</Button>
                    </Space>
                );
            case 'custom':
                return (
                    <div className="flex items-center gap-2">
                        <TableOutlined />
                        <Text>Custom Content Here</Text>
                    </div>
                );
            default:
                return undefined;
        }
    }, [topLeftContent]);

    return (
        <PageController route={Route}>
            <div className="p-6">
                <Row gutter={24}>
                    {/* Configuration Panel */}
                    <Col xs={24} lg={8} xl={6}>
                        <Card
                            title={
                                <div className="flex items-center justify-between">
                                    <Text strong>Configuration</Text>
                                    <Space>
                                        <Tooltip title="Copy Configuration">
                                            <Button
                                                type="tertiary"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                onClick={copyConfig}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Reset to Default">
                                            <Button
                                                type="tertiary"
                                                size="small"
                                                icon={<ReloadOutlined />}
                                                onClick={resetConfig}
                                            />
                                        </Tooltip>
                                    </Space>
                                </div>
                            }
                            className="sticky top-4"
                            style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}
                        >
                            {/* Presets */}
                            <div className="mb-4">
                                <Text strong>Presets:</Text>
                                <div className="mt-2 space-y-2">
                                    {Object.entries(presets).map(([key, preset]) => (
                                        <Button key={key} size="small" block onClick={() => applyPreset(key)}>
                                            {preset.name}
                                        </Button>
                                    ))}
                                </div>
                            </div>

                            <Divider />

                            <Form
                                form={form}
                                layout="vertical"
                                initialValues={defaultConfig}
                                onValuesChange={handleConfigChange}
                                size="small"
                            >
                                <Collapse defaultActiveKey={['pagination', 'columns', 'selection']}>
                                    {/* Pagination Settings */}
                                    <Panel header="Pagination" key="pagination">
                                        <Form.Item label="Position" name="paginationPosition">
                                            <Radio.Group>
                                                <Radio value="top">Top</Radio>
                                                <Radio value="bottom">Bottom</Radio>
                                                <Radio value="both">Both</Radio>
                                                <Radio value="none">None</Radio>
                                            </Radio.Group>
                                        </Form.Item>

                                        <Form.Item label="Variant" name="paginationVariant">
                                            <Radio.Group>
                                                <Radio value="standard">Standard</Radio>
                                                <Radio value="simple">Simple</Radio>
                                                <Radio value="compact">Compact</Radio>
                                            </Radio.Group>
                                        </Form.Item>

                                        <Form.Item name="showSizeChanger" valuePropName="checked">
                                            <Checkbox>Show Size Changer</Checkbox>
                                        </Form.Item>

                                        <Form.Item name="showQuickJumper" valuePropName="checked">
                                            <Checkbox>Show Quick Jumper</Checkbox>
                                        </Form.Item>

                                        <Form.Item name="showTotal" valuePropName="checked">
                                            <Checkbox>Show Total</Checkbox>
                                        </Form.Item>

                                        <Form.Item label="Default Page Size" name="defaultPageSize">
                                            <InputNumber min={5} max={100} step={5} style={{ width: '100%' }} />
                                        </Form.Item>
                                    </Panel>

                                    {/* Column Visibility Settings */}
                                    <Panel header="Column Visibility" key="columns">
                                        <Form.Item name="columnVisibilityEnabled" valuePropName="checked">
                                            <Checkbox>Enable Column Visibility</Checkbox>
                                        </Form.Item>

                                        <Form.Item name="enableReordering" valuePropName="checked">
                                            <Checkbox>Enable Column Reordering</Checkbox>
                                        </Form.Item>

                                        <Form.Item label="Default Hidden Columns" name="defaultHidden">
                                            <Select
                                                mode="multiple"
                                                placeholder="Select columns to hide"
                                                options={columns
                                                    .filter((col: any) => col.meta?.hideable !== false)
                                                    .map((col: any) => ({
                                                        label: col.header as string,
                                                        value: col.id,
                                                    }))}
                                            />
                                        </Form.Item>
                                    </Panel>

                                    {/* Row Selection Settings */}
                                    <Panel header="Row Selection" key="selection">
                                        <Form.Item name="rowSelectionEnabled" valuePropName="checked">
                                            <Checkbox>Enable Row Selection</Checkbox>
                                        </Form.Item>

                                        <Form.Item label="Selection Type" name="selectionType">
                                            <Radio.Group>
                                                <Radio value="checkbox">Checkbox</Radio>
                                                <Radio value="radio">Radio</Radio>
                                            </Radio.Group>
                                        </Form.Item>

                                        <Form.Item name="preserveSelectedRowKeys" valuePropName="checked">
                                            <Checkbox>Preserve Selection on Page Change</Checkbox>
                                        </Form.Item>
                                    </Panel>

                                    {/* Refresh Settings */}
                                    <Panel header="Refresh" key="refresh">
                                        <Form.Item name="refreshEnabled" valuePropName="checked">
                                            <Checkbox>Enable Refresh Button</Checkbox>
                                        </Form.Item>

                                        <Form.Item label="Auto Refresh (ms)" name="autoRefreshInterval">
                                            <InputNumber
                                                min={0}
                                                max={60000}
                                                step={1000}
                                                style={{ width: '100%' }}
                                                placeholder="0 = disabled"
                                            />
                                        </Form.Item>
                                    </Panel>

                                    {/* Table Appearance */}
                                    <Panel header="Appearance" key="appearance">
                                        <Form.Item label="Size" name="size">
                                            <Radio.Group>
                                                <Radio value="small">Small</Radio>
                                                <Radio value="middle">Middle</Radio>
                                                <Radio value="large">Large</Radio>
                                            </Radio.Group>
                                        </Form.Item>

                                        <Form.Item name="bordered" valuePropName="checked">
                                            <Checkbox>Bordered</Checkbox>
                                        </Form.Item>

                                        <Form.Item name="sticky" valuePropName="checked">
                                            <Checkbox>Sticky Header</Checkbox>
                                        </Form.Item>

                                        <Form.Item name="showHeader" valuePropName="checked">
                                            <Checkbox>Show Header</Checkbox>
                                        </Form.Item>

                                        <Form.Item name="showSorterTooltip" valuePropName="checked">
                                            <Checkbox>Show Sorter Tooltip</Checkbox>
                                        </Form.Item>

                                        <Form.Item name="expandableEnabled" valuePropName="checked">
                                            <Checkbox>Enable Expandable Rows</Checkbox>
                                        </Form.Item>
                                    </Panel>

                                    {/* Custom Content */}
                                    <Panel header="Custom Content" key="custom">
                                        <Form.Item label="Top Left Content">
                                            <Radio.Group
                                                value={topLeftContent}
                                                onChange={(e) => setTopLeftContent(e.target.value)}
                                            >
                                                <Radio value="none">None</Radio>
                                                <Radio value="button">Action Buttons</Radio>
                                                <Radio value="custom">Custom Content</Radio>
                                            </Radio.Group>
                                        </Form.Item>
                                    </Panel>
                                </Collapse>
                            </Form>
                        </Card>
                    </Col>

                    {/* Table Preview */}
                    <Col xs={24} lg={16} xl={18}>
                        <Card
                            title={
                                <div className="flex items-center gap-2">
                                    <TableOutlined />
                                    <Text strong>Table Preview</Text>
                                </div>
                            }
                        >
                            <PrismaTable {...tableProps} topLeftContent={getTopLeftContent} />
                        </Card>

                        {/* Code Example */}
                        <Card title="Code Example" className="mt-4">
                            <pre className="overflow-x-auto rounded bg-muted p-4">
                                <code className="text-sm">
                                    {`const tableProps = usePrismaTable({
    data: data?.rows || [],
    total: data?.total || 0,
    loading: isLoading,
    columns,
    rowKey: 'id',
    pagination: {
        position: '${config.paginationPosition}',
        variant: '${config.paginationVariant}',
        showSizeChanger: ${config.showSizeChanger},
        showQuickJumper: ${config.showQuickJumper},
        showTotal: ${config.showTotal},
        defaultPageSize: ${config.defaultPageSize},
    },
    columnVisibility: ${
        config.columnVisibilityEnabled
            ? `{
        enabled: true,
        persistKey: 'my-table-columns',
        enableReordering: ${config.enableReordering},
        defaultHidden: ${JSON.stringify(config.defaultHidden)},
    }`
            : 'undefined'
    },
    rowSelection: ${
        config.rowSelectionEnabled
            ? `{
        enabled: true,
        type: '${config.selectionType}',
        preserveSelectedRowKeys: ${config.preserveSelectedRowKeys},
    }`
            : 'undefined'
    },
    size: '${config.size}',
    bordered: ${config.bordered},
    sticky: ${config.sticky},
    showHeader: ${config.showHeader},
    showSorterTooltip: ${config.showSorterTooltip},
});

return <PrismaTable {...tableProps} />;`}
                                </code>
                            </pre>
                        </Card>
                    </Col>
                </Row>
            </div>
        </PageController>
    );
}
